import { z } from '@repo/schema'

export const RefreshTokenResponseSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number().positive(),
})

export type RefreshAuthTokenResult =
  | { status: 'refreshed'; token: string }
  /** The API refused the token (401/403): the session is over. */
  | { status: 'rejected' }
  /** Network error, timeout, other error status or a malformed body: try again later. */
  | { status: 'unavailable' }

/**
 * Exchanges a token for a new one. The API accepts an expired token while it is
 * inside the refresh window, and revokes the old token once it is refreshed.
 */
export async function refreshAuthToken({
  apiUrl,
  source,
  token,
}: {
  apiUrl: string
  source: string
  token: string
}): Promise<RefreshAuthTokenResult> {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    // Races the timeout rather than relying on the abort, which a stalled body may ignore.
    const { body, response } = await Promise.race([
      (async () => {
        const response = await fetch(`${apiUrl}/auth/refresh`, {
          headers: { 'App-Source': source, Authorization: `Bearer ${token}` },
          method: 'POST',
          signal: controller.signal,
        })
        const body: unknown = response.ok ? await response.json() : null
        return { body, response }
      })(),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort()
          reject(new Error('Token refresh timed out'))
        }, 10_000)
      }),
    ])

    if (response.status === 401 || response.status === 403) return { status: 'rejected' }
    if (!response.ok) return { status: 'unavailable' }

    const refreshed = RefreshTokenResponseSchema.safeParse(body)
    return refreshed.success
      ? { status: 'refreshed', token: refreshed.data.access_token }
      : { status: 'unavailable' }
  } catch {
    // Intentionally reported as unavailable: the caller keeps the session and retries later.
    return { status: 'unavailable' }
  } finally {
    clearTimeout(timeoutId)
  }
}
